/*
  Table helpers shared by <DataTable /> and pages that sort / group rows themselves.

    import { compareValues, sortRows, parseSortParam, formatSortParam, nextSort } from '@/lib/table';

  compareValues(a, b)       natural order: numbers numerically, text case-insensitively with numbers
                             inside text in order ("VO-2" < "VO-10"), Dates by time; empty values
                             (null, undefined, '', '—') always sort LAST, in both directions.
  sortRows(rows, sort, getValue)
                             stable sort; sort = { key, dir: 'asc' | 'desc' } or null (rows unchanged);
                             getValue(row, key) reads the value (default row[key]).
  parseSortParam('due:desc') → { key: 'due', dir: 'desc' } (null for '' / bad input)
  formatSortParam({ key: 'due', dir: 'desc' }) → 'due:desc' — for ?sort= in the URL (useUrlState).
  nextSort(current, key, firstDir = 'asc') → the sort after clicking a header: a new column starts at
                             firstDir, the same column flips.
  groupRows(rows, groupBy)  → [{ key, label, rows }] in first-appearance order.
*/

const EMPTY_TEXT = new Set(['', '—', '-', '–']);

export function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'number') return Number.isNaN(value);
  if (typeof value === 'string') return EMPTY_TEXT.has(value.trim());
  if (value instanceof Date) return Number.isNaN(value.getTime());
  return false;
}

const collator = typeof Intl !== 'undefined'
  ? new Intl.Collator('en', { numeric: true, sensitivity: 'base' })
  : null;

function toComparable(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'boolean') return value ? 1 : 0;
  return value;
}

/** Ascending comparison; empty values last. Use `-compareValues(a, b)` only for non-empty values. */
export function compareValues(a, b) {
  const aEmpty = isEmptyValue(a);
  const bEmpty = isEmptyValue(b);
  if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
  const x = toComparable(a);
  const y = toComparable(b);
  if (typeof x === 'number' && typeof y === 'number') return x - y;
  const xs = String(x);
  const ys = String(y);
  return collator ? collator.compare(xs, ys) : xs.localeCompare(ys);
}

const defaultGetValue = (row, key) => (row == null ? undefined : row[key]);

/** Stable sort that keeps empty values at the bottom in both directions. */
export function sortRows(rows, sort, getValue = defaultGetValue) {
  const list = Array.isArray(rows) ? rows : [];
  if (!sort || !sort.key) return list;
  const factor = sort.dir === 'desc' ? -1 : 1;
  return list
    .map((row, index) => ({ row, index, value: getValue(row, sort.key) }))
    .sort((left, right) => {
      const leftEmpty = isEmptyValue(left.value);
      const rightEmpty = isEmptyValue(right.value);
      if (leftEmpty || rightEmpty) {
        if (leftEmpty !== rightEmpty) return leftEmpty ? 1 : -1;
        return left.index - right.index;
      }
      return (compareValues(left.value, right.value) * factor) || left.index - right.index;
    })
    .map((entry) => entry.row);
}

export function parseSortParam(value) {
  if (!value || typeof value !== 'string') return null;
  const [key, dir] = value.split(':');
  if (!key) return null;
  return { key, dir: dir === 'desc' ? 'desc' : 'asc' };
}

export function formatSortParam(sort) {
  if (!sort || !sort.key) return '';
  return `${sort.key}:${sort.dir === 'desc' ? 'desc' : 'asc'}`;
}

export function nextSort(current, key, firstDir = 'asc') {
  if (!current || current.key !== key) return { key, dir: firstDir === 'desc' ? 'desc' : 'asc' };
  return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
}

/** groupBy(row) returns a key string or { key, label }. Groups keep the order rows first appear in. */
export function groupRows(rows, groupBy) {
  const groups = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const raw = groupBy(row);
    const key = raw && typeof raw === 'object' ? String(raw.key) : String(raw ?? '');
    const label = raw && typeof raw === 'object' ? (raw.label ?? raw.key) : (raw ?? '');
    if (!groups.has(key)) groups.set(key, { key, label, rows: [] });
    groups.get(key).rows.push(row);
  });
  return [...groups.values()];
}
